import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { chainContinuationKeys } from "@/api/queries/chainContinuationKeys";
import type { InteractiveInputState } from "@/api/types";
import { apiFetch } from "@/lib/api";

const API = "/api/v1";

export interface CustomerProjectLine {
  id: number;
  material_code: string | null;
  // Variant identity lives in ``dimensions`` — no axis is a named field.
  quantity: number;
  dimensions?: Record<string, unknown>;
  unit_price_czk: number;
  line_total_czk: number;
  config_snapshot: Record<string, unknown> | null;
}

export interface CustomerProjectOperation {
  id: number;
  process_type: string;
  order_index: number;
  template_node_id: string | null;
  fulfillment_partner_id: number | null;
  config_snapshot: Record<string, unknown> | null;
  /** KAN-1708 — interaktivní vstupy primárky; náhled z nich bere uloženou volbu. */
  interactive_inputs?: InteractiveInputState[] | null;
  public_input_dimension_keys: string[];
  unit_price_czk: number;
  estimated_duration_seconds: number;
  // KAN-459: ``estimated_duration_seconds`` cannot tell an authored ``0`` from
  // a step nobody has estimated yet (NULL on the row, KAN-267) — both arrive as
  // a number. This flag is the difference. It is data, not copy: whether the
  // customer sees "bez odhadu" is a product decision that has not been made.
  duration_estimate_missing?: boolean;
}

export interface CustomerProjectItem {
  id: string;
  file_name: string;
  process_type: string;
  preview_url: string | null;
  mesh_preview_url: string | null;
  analysis: Record<string, unknown>;
  lines: CustomerProjectLine[];
  operations: CustomerProjectOperation[];
}

export interface CustomerProject {
  public_id: string;
  name: string;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  item_count: number;
  items: CustomerProjectItem[];
  /** KAN-303 — visible ownership: "customer" (tied to the buyer's
   *  CustomerOrg) or "admin" (tenant admin, not tied to one buyer).
   *  Server-derived at creation time, never client-settable. Optional
   *  on the FE type so existing test fixtures
   *  built before this field shipped keep compiling. */
  owner_scope?: "customer" | "admin";
  /** KAN-310 — skladovost. Only a tenant admin with the ``stock``/
   *  ``write`` permission can flip it (``PATCH .../stockability``), and
   *  only on an ``owner_scope: "admin"`` definition. Optional on the FE
   *  type for the same reason as ``owner_scope``. */
  is_stockable?: boolean;
}

export interface CustomerProjectsResponse {
  total: number;
  page: number;
  page_size: number;
  items: CustomerProject[];
}

export interface ProjectSkippedLine {
  file_name: string;
  reason: string;
  code: string;
}

export interface ProjectAddToCartResponse {
  added_lines: number;
  skipped_lines: ProjectSkippedLine[];
}

export interface ProjectLinePayload {
  material_code?: string | null;
  quantity?: number | null;
  /** Universal per-process variant dimensions — the canonical channel;
   *  the wire contract carries no typed per-process fields. */
  dimensions?: Record<string, unknown>;
  extra_config?: Record<string, unknown>;
}

export interface ProjectChainContinuation {
  name: string;
  slug: string;
  display_name: string;
  description: string;
  icon: string;
  color: string;
  template_node_id: string | null;
  public_input_dimension_keys: string[];
}

export function useCustomerProjects(
  params: { page?: number; pageSize?: number } = {},
  options?: Omit<UseQueryOptions<CustomerProjectsResponse>, "queryKey" | "queryFn">,
) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  return useQuery<CustomerProjectsResponse>({
    queryKey: ["projects", page, pageSize],
    queryFn: () =>
      apiFetch<CustomerProjectsResponse>(
        `${API}/projects?page=${page}&page_size=${pageSize}`,
      ),
    staleTime: 10 * 1000,
    retry: false,
    ...options,
  });
}

export function useCustomerProject(
  publicId: string | null,
  options?: Omit<UseQueryOptions<CustomerProject>, "queryKey" | "queryFn">,
) {
  return useQuery<CustomerProject>({
    queryKey: ["project", publicId ?? ""],
    queryFn: () => apiFetch<CustomerProject>(`${API}/projects/${publicId}`),
    enabled: publicId !== null && publicId !== "",
    staleTime: 10 * 1000,
    retry: false,
    ...options,
  });
}

export function useCreateCustomerProject(
  options?: Omit<
    UseMutationOptions<CustomerProject, Error, { name: string; description?: string | null }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<CustomerProject, Error, { name: string; description?: string | null }>({
    mutationFn: (body) =>
      apiFetch<CustomerProject>(`${API}/projects`, { method: "POST", body }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

export function useUpdateCustomerProject(
  projectId: string,
  options?: Omit<
    UseMutationOptions<CustomerProject, Error, { name?: string; description?: string | null }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<CustomerProject, Error, { name?: string; description?: string | null }>({
    mutationFn: (body) =>
      apiFetch<CustomerProject>(`${API}/projects/${projectId}`, { method: "PATCH", body }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["project", projectId] });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

export function useUploadProjectItem(
  projectId: string,
  options?: Omit<
    UseMutationOptions<
      CustomerProjectItem,
      Error,
      { file: File; process_type?: string }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<
    CustomerProjectItem,
    Error,
    { file: File; process_type?: string }
  >({
    mutationFn: async ({ file, process_type }) => {
      const body = new FormData();
      body.append("file", file);
      const qs = process_type ? `?process_type=${encodeURIComponent(process_type)}` : "";
      const res = await apiFetch<{ item: CustomerProjectItem }>(
        `${API}/projects/${projectId}/items${qs}`,
        { method: "POST", body, credentials: "include" },
      );
      return res.item;
    },
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["project", projectId] });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

export function useDeleteProjectItem(
  projectId: string,
  options?: Omit<UseMutationOptions<void, Error, string>, "mutationFn">,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<void, Error, string>({
    mutationFn: (itemSlug) =>
      apiFetch<void>(`${API}/projects/${projectId}/items/${itemSlug}`, { method: "DELETE" }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["project", projectId] });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

export function useAddProjectLine(
  projectId: string,
  options?: Omit<
    UseMutationOptions<
      CustomerProjectLine,
      Error,
      { itemSlug: string; payload: ProjectLinePayload }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<
    CustomerProjectLine,
    Error,
    { itemSlug: string; payload: ProjectLinePayload }
  >({
    mutationFn: ({ itemSlug, payload }) =>
      apiFetch<CustomerProjectLine>(`${API}/projects/${projectId}/items/${itemSlug}/lines`, {
        method: "POST",
        body: payload,
      }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["project", projectId] });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

export function useUpdateProjectLine(
  projectId: string,
  options?: Omit<
    UseMutationOptions<
      CustomerProjectLine,
      Error,
      { lineId: number; payload: ProjectLinePayload }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<
    CustomerProjectLine,
    Error,
    { lineId: number; payload: ProjectLinePayload }
  >({
    mutationFn: ({ lineId, payload }) =>
      apiFetch<CustomerProjectLine>(`${API}/projects/${projectId}/lines/${lineId}`, {
        method: "PATCH",
        body: payload,
      }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["project", projectId] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

export function useDeleteProjectLine(
  projectId: string,
  options?: Omit<UseMutationOptions<void, Error, number>, "mutationFn">,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<void, Error, number>({
    mutationFn: (lineId) =>
      apiFetch<void>(`${API}/projects/${projectId}/lines/${lineId}`, { method: "DELETE" }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["project", projectId] });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

export function useProjectChainContinuations(
  projectId: string,
  itemSlug: string | null,
  options?: Omit<UseQueryOptions<ProjectChainContinuation[]>, "queryKey" | "queryFn">,
) {
  return useQuery<ProjectChainContinuation[]>({
    queryKey: chainContinuationKeys.project(projectId, itemSlug ?? ""),
    queryFn: () =>
      apiFetch<ProjectChainContinuation[]>(
        `${API}/projects/${projectId}/items/${itemSlug}/chain-continuations`,
      ),
    enabled: Boolean(projectId && itemSlug),
    staleTime: 10 * 1000,
    ...options,
  });
}

export function useAddProjectOperation(
  projectId: string,
  options?: Omit<
    UseMutationOptions<
      CustomerProjectOperation,
      Error,
      {
        itemSlug: string;
        payload: { process_type: string; template_node_id?: string | null; config?: Record<string, unknown> | null };
      }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<
    CustomerProjectOperation,
    Error,
    {
      itemSlug: string;
      payload: { process_type: string; template_node_id?: string | null; config?: Record<string, unknown> | null };
    }
  >({
    mutationFn: ({ itemSlug, payload }) =>
      apiFetch<CustomerProjectOperation>(`${API}/projects/${projectId}/items/${itemSlug}/operations`, {
        method: "POST",
        body: payload,
      }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["project", projectId] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

export function useUpdateProjectOperation(
  projectId: string,
  options?: Omit<
    UseMutationOptions<
      CustomerProjectOperation,
      Error,
      { operationId: number; config: Record<string, unknown> }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<
    CustomerProjectOperation,
    Error,
    { operationId: number; config: Record<string, unknown> }
  >({
    mutationFn: ({ operationId, config }) =>
      apiFetch<CustomerProjectOperation>(`${API}/projects/${projectId}/operations/${operationId}`, {
        method: "PATCH",
        body: { config },
      }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["project", projectId] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

export function useDeleteProjectOperation(
  projectId: string,
  options?: Omit<UseMutationOptions<void, Error, number>, "mutationFn">,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<void, Error, number>({
    mutationFn: (operationId) =>
      apiFetch<void>(`${API}/projects/${projectId}/operations/${operationId}`, { method: "DELETE" }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["project", projectId] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

export function useAddProjectToCart(
  projectId: string,
  options?: Omit<
    UseMutationOptions<
      ProjectAddToCartResponse,
      Error,
      {
        item_slugs: string[];
        material_code?: string | null;
        quantity?: number | null;
        dimensions?: Record<string, unknown>;
        extra_config?: Record<string, unknown>;
      }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation({
    mutationFn: (body) =>
      apiFetch<ProjectAddToCartResponse>(`${API}/projects/${projectId}/add-to-cart`, {
        method: "POST",
        body,
      }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["cart"] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

/** KAN-303 — "order N more sets of this saved assembly", no re-upload. */
export function useMaterializeProjectDefinition(
  projectId: string,
  options?: Omit<
    UseMutationOptions<
      ProjectAddToCartResponse,
      Error,
      { rootItemSlug: string; quantity: number }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<
    ProjectAddToCartResponse,
    Error,
    { rootItemSlug: string; quantity: number }
  >({
    mutationFn: ({ rootItemSlug, quantity }) =>
      apiFetch<ProjectAddToCartResponse>(`${API}/projects/${projectId}/materialize`, {
        method: "POST",
        body: { root_item_slug: rootItemSlug, quantity },
      }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["cart"] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}

export function useSaveOrderAsProject(
  options?: Omit<
    UseMutationOptions<CustomerProject, Error, { orderNumber: string; name?: string | null }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<CustomerProject, Error, { orderNumber: string; name?: string | null }>({
    mutationFn: ({ orderNumber, name }) =>
      apiFetch<CustomerProject>(`${API}/orders/${orderNumber}/save-as-project`, {
        method: "POST",
        body: { name },
      }),
    ...restOptions,
    onSuccess: async (data, variables, ctx, mutationCtx) => {
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await callerOnSuccess?.(data, variables, ctx, mutationCtx);
    },
  });
}
